/**
 * Cliente HTTP do front.
 *
 * Duas responsabilidades, e nenhuma além delas: anexar o token CSRF nas
 * requisições mutantes e transformar falha em erro tipado. **Não** faz retry:
 * a API é local (mesmo origem, mesmo processo) e um retry cego esconderia o
 * problema real de quem está olhando a tela.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** Código estável da API — é o que a interface traduz, nunca a mensagem crua. */
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(`${String(status)} ${code}`);
    this.name = 'ApiError';
  }
}

const CSRF_COOKIE = 'cqp_csrf';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function csrfToken(): string | null {
  const match = new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`).exec(document.cookie);
  return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}

export interface RequestOptions {
  readonly method?: string | undefined;
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['content-type'] = 'application/json';

  /**
   * Token CSRF **quando existe**, e nunca uma recusa própria.
   *
   * O servidor exige CSRF só de requisição que carrega sessão de usuário. Três
   * entradas do sistema acontecem **sem sessão e sem cookie**, por definição:
   * login, aceite de convite e pareamento da TV. Uma versão
   * anterior lançava `csrf_token_missing` aqui quando o cookie faltava, e o efeito
   * foi o pior possível: **nenhum navegador novo conseguia entrar no sistema**. O
   * POST de login nem saía da página, e a tela dizia "Sessão desatualizada.
   * Recarregue a página" — recarregar não resolvia nada, porque o cookie só nasce
   * como resposta do próprio login. O mesmo acontecia no pareamento da TV, que
   * ainda por cima exibia "código inválido ou expirado" para um código válido.
   *
   * Quem decide é o servidor: mandamos o que temos. Se houver sessão e o cookie
   * estiver quebrado, a resposta é um 403 `csrf_token_invalid` de verdade — um
   * diagnóstico correto, em vez de um erro inventado no cliente.
   */
  if (MUTATING.has(method)) {
    const token = csrfToken();
    if (token !== null) headers['x-csrf-token'] = token;
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text === '' ? null : safeJson(text);

  if (!response.ok) {
    const code =
      isRecord(payload) && typeof payload.error === 'string' ? payload.error : 'request_failed';
    throw new ApiError(response.status, code, payload);
  }

  return payload as T;
}

/**
 * Envio de **arquivo cru** — hoje, o toque da chamada (ADR-0026).
 *
 * Não passa por `api()` porque lá o corpo é serializado como JSON: aqui o corpo *é*
 * o arquivo, e o `content-type` dele é o que o servidor usa para aceitar ou recusar.
 * O nome do arquivo viaja num cabeçalho, e serve só de rótulo na tela de
 * configurações.
 */
export async function apiRaw(
  path: string,
  file: Blob,
  options: { readonly contentType: string; readonly fileName?: string },
): Promise<void> {
  const headers: Record<string, string> = { 'content-type': options.contentType };
  const token = csrfToken();
  if (token !== null) headers['x-csrf-token'] = token;
  if (options.fileName !== undefined) headers['x-chime-filename'] = options.fileName;

  const response = await fetch(path, {
    method: 'POST',
    headers,
    body: file,
    credentials: 'same-origin',
  });

  if (!response.ok) {
    const text = await response.text();
    const payload: unknown = text === '' ? null : safeJson(text);
    const code =
      isRecord(payload) && typeof payload.error === 'string' ? payload.error : 'request_failed';
    throw new ApiError(response.status, code, payload);
  }
}

/**
 * Requisição sem corpo de resposta (204 e afins).
 *
 * Existe para não usar `void` como argumento de tipo — que é ambíguo e sinalizado
 * pelo lint — e para deixar explícito, na chamada, que não há nada a ler.
 */
export async function apiVoid(path: string, options: RequestOptions = {}): Promise<void> {
  await api<unknown>(path, options);
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // Resposta não-JSON num endpoint de API significa que algo serviu HTML no
    // lugar — tipicamente um fallback de SPA mal configurado. Melhor relatar do
    // que estourar num parse incompreensível.
    return { error: 'invalid_response_body' };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
